import React, { useEffect, useRef } from 'react';
import styles from './PlayerTrackingCanvas.module.scss';

/**
 * OccupancyHeatmapCanvas renders a Yellow→Orange→Red density heatmap of player positions
 * over the exact same badminton court background used by PlayerTrackingCanvas.
 *
 * Props:
 * - data: Array<{ RallyID, player_side, frame, mid_x, mid_y }>
 * - width: number (px)
 * - height: number (px)
 * - style: optional style overrides for container
 * - playerFilter: undefined | 'A' | 'B' (if set, only include that player's points)
 * - percentileClamp: [low, high] percentiles for normalization (default [0.02, 0.98])
 * - gridSize: {cols, rows} number of bins across court (default { cols: 160, rows: 320 })
 */
const OccupancyHeatmapCanvas = React.memo(function OccupancyHeatmapCanvas({
  data = [],
  width = 500,
  height = 800,
  style,
  playerFilter,
  percentileClamp = [0.02, 0.98],
  gridSize = { cols: 160, rows: 320 },
  showLegend = true,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = React.useState({ width, height });

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      setCanvasSize({ width, height });
      return;
    }

    const updateSize = () => {
      const nextWidth = Math.max(1, Math.round(node.clientWidth || width));
      const nextHeight = Math.max(1, Math.round(node.clientHeight || height));
      setCanvasSize((prev) => (
        prev.width === nextWidth && prev.height === nextHeight
          ? prev
          : { width: nextWidth, height: nextHeight }
      ));
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);

    return () => observer.disconnect();
  }, [width, height]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Reset any previous transforms and clear canvas
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // --- Court dimensions (exactly match PlayerTrackingCanvas) ---
    const court = {
      totalLength: 13.40,         // L
      totalWidth: 6.10,           // W
      singlesWidth: 5.18,         // Ws
      lineWidth: 0.04,
      shortServiceFromNet: 1.98,  // distance from net
      longServiceFromBack: 0.76,  // doubles long service line from back boundary
      singlesOffsetFromEdge: 0.46 // (W - Ws)/2
    };

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const gradient = ctx.createRadialGradient(
      centerX, centerY, 0,
      centerX, centerY, Math.max(centerX, centerY) * 1.3
    );
    gradient.addColorStop(0, '#22c55e'); // Vibrant vibrant green center
    gradient.addColorStop(0.6, '#15803d'); // Transition
    gradient.addColorStop(1, '#0b4d26'); // Deeper, more "lush" dark green at edges
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const courtAspectRatio = court.totalWidth / court.totalLength;
    const canvasAspectRatio = canvas.width / canvas.height;
    let scale, courtPixelWidth, courtPixelHeight;
    if (canvasAspectRatio > courtAspectRatio) {
      courtPixelHeight = canvas.height;
      scale = courtPixelHeight / court.totalLength;
      courtPixelWidth = scale * court.totalWidth;
    } else {
      courtPixelWidth = canvas.width;
      scale = courtPixelWidth / court.totalWidth;
      courtPixelHeight = scale * court.totalLength;
    }
    const offsetX = (canvas.width - courtPixelWidth) / 2;
    const offsetY = (canvas.height - courtPixelHeight) / 2;

    ctx.save();
    ctx.translate(offsetX, offsetY);

    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = Math.max(0.8, scale * court.lineWidth * 0.85);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.globalAlpha = 0.95;

    // Helper: map meters to pixels within translated court
    const scaleToCourt = (value, isX) => {
      if (isX) return (value / court.totalWidth) * courtPixelWidth;
      return (value / court.totalLength) * courtPixelHeight;
    };

    // --- Draw all SOLID lines in one single operation for perfect joins ---
    ctx.beginPath();

    // 1. Outer boundary (doubles)
    ctx.rect(0, 0, courtPixelWidth, courtPixelHeight);

    // 2. Singles sidelines (inner verticals, full length)
    const singlesSidelineX1 = scale * court.singlesOffsetFromEdge;
    const singlesSidelineX2 = courtPixelWidth - (scale * court.singlesOffsetFromEdge);
    ctx.moveTo(singlesSidelineX1, 0);
    ctx.lineTo(singlesSidelineX1, courtPixelHeight);
    ctx.moveTo(singlesSidelineX2, 0);
    ctx.lineTo(singlesSidelineX2, courtPixelHeight);

    // 3. Long service lines for doubles (0.76m from back)
    const longServiceLineYTop = scale * court.longServiceFromBack;
    const longServiceLineYBot = courtPixelHeight - (scale * court.longServiceFromBack);
    ctx.moveTo(0, longServiceLineYTop);
    ctx.lineTo(courtPixelWidth, longServiceLineYTop);
    ctx.moveTo(0, longServiceLineYBot);
    ctx.lineTo(courtPixelWidth, longServiceLineYBot);

    // 4. Short service lines (net ± 1.98m)
    const halfLength = courtPixelHeight / 2;
    const shortServiceOffset = scale * court.shortServiceFromNet;
    const shortServiceLineYTop = halfLength - shortServiceOffset;
    const shortServiceLineYBot = halfLength + shortServiceOffset;
    ctx.moveTo(0, shortServiceLineYTop);
    ctx.lineTo(courtPixelWidth, shortServiceLineYTop);
    ctx.moveTo(0, shortServiceLineYBot);
    ctx.lineTo(courtPixelWidth, shortServiceLineYBot);

    // 5. Center partition lines
    const halfWidth = courtPixelWidth / 2;
    // Top: boundary to long service
    ctx.moveTo(halfWidth, 0);
    ctx.lineTo(halfWidth, longServiceLineYTop);
    // Top: long service to short service
    ctx.moveTo(halfWidth, shortServiceLineYTop);
    ctx.lineTo(halfWidth, longServiceLineYTop);
    // Bottom: long service to boundary
    ctx.moveTo(halfWidth, longServiceLineYBot);
    ctx.lineTo(halfWidth, courtPixelHeight);
    // Bottom: long service to short service
    ctx.moveTo(halfWidth, longServiceLineYBot);
    ctx.lineTo(halfWidth, shortServiceLineYBot);

    // Render solid lines
    ctx.stroke();

    // --- Draw the Net (Dashed White Line) ---
    ctx.save();
    const netY = Math.round(courtPixelHeight / 2) + 0.5;
    ctx.setLineDash([scale * 0.25, scale * 0.25]);
    ctx.beginPath();
    ctx.moveTo(0, netY);
    ctx.lineTo(courtPixelWidth, netY);
    ctx.stroke();
    ctx.restore();

    // --- Build heatmap grid in court pixel space ---
    const cols = gridSize.cols;
    const rows = gridSize.rows;
    const cellW = courtPixelWidth / cols;
    const cellH = courtPixelHeight / rows;
    const grid = new Float32Array(cols * rows);

    const filtered = Array.isArray(data)
      ? data.filter(d => {
        if (!d) return false;
        const side = (d.player_side || d.side || d.player || '').toString().trim();
        if (playerFilter && side !== playerFilter) return false;
        const x = Number(d.mid_x);
        const y = Number(d.mid_y);
        return Number.isFinite(x) && Number.isFinite(y);
      })
      : [];

    // Accumulate counts in bins
    for (const d of filtered) {
      const xPx = scaleToCourt(Number(d.mid_x), true);
      const yPx = scaleToCourt(Number(d.mid_y), false);
      const cx = Math.floor((xPx / courtPixelWidth) * cols);
      const cy = Math.floor((yPx / courtPixelHeight) * rows);
      if (cx >= 0 && cx < cols && cy >= 0 && cy < rows) {
        grid[cy * cols + cx] += 1;
      }
    }

    // Optional: box blur with configurable radius to increase splat size
    const blurOnce = (arr, w, h, radius = 1) => {
      const out = new Float32Array(arr.length);
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          let sum = 0, cnt = 0;
          for (let dy = -radius; dy <= radius; dy++) {
            for (let dx = -radius; dx <= radius; dx++) {
              const nx = x + dx;
              const ny = y + dy;
              if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
                sum += arr[ny * w + nx];
                cnt++;
              }
            }
          }
          out[y * w + x] = sum / cnt;
        }
      }
      return out;
    };

    // Increase effective splat radius: wider kernel and multiple passes for professional look
    const kernelRadius = 3; // Increased from 2 for smoother, broader "splats"
    let blurred = blurOnce(grid, cols, rows, kernelRadius);
    blurred = blurOnce(blurred, cols, rows, kernelRadius);
    blurred = blurOnce(blurred, cols, rows, 2); // Third partial pass for final smoothing

    // Compute normalization range with percentile clamp
    const values = Array.from(blurred);
    values.sort((a, b) => a - b);
    const lowIdx = Math.floor(values.length * percentileClamp[0]);
    const highIdx = Math.ceil(values.length * percentileClamp[1]);
    const vMin = values[lowIdx] || 0;
    const vMax = values[highIdx] || 1;

    const norm = v => {
      if (vMax === vMin) return 0;
      let t = (v - vMin) / (vMax - vMin);
      t = Math.max(0, Math.min(1, t));
      return t;
    };

    // Color ramp: Yellow (low) -> Orange (mid) -> Red (high)
    const colorFor = t => {
      // piecewise linear between stops: yellow #FFFF00, orange #FFA500, red #FF0000
      // 0..0.5: yellow->orange, 0.5..1: orange->red
      const lerp = (a, b, u) => a + (b - a) * u;
      if (t <= 0.5) {
        const u = t / 0.5;
        const r = lerp(255, 255, u);
        const g = lerp(255, 165, u);
        const b = lerp(0, 0, u);
        return [r, g, b];
      } else {
        const u = (t - 0.5) / 0.5;
        const r = lerp(255, 255, u);
        const g = lerp(165, 0, u);
        const b = lerp(0, 0, u);
        return [r, g, b];
      }
    };

    // Draw heatmap cells with alpha based on normalized intensity
    ctx.globalCompositeOperation = 'source-over';
    const alphaScale = 0.85; // overall opacity of heat layer
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const v = blurred[y * cols + x];
        if (v <= vMin) continue; // skip empty/low bins
        const t = norm(v);
        const [r, g, b] = colorFor(t);
        ctx.fillStyle = `rgba(${r | 0}, ${g | 0}, ${b | 0}, ${alphaScale * t})`;
        ctx.fillRect(x * cellW, y * cellH, cellW, cellH);
      }
    }

    // Draw legend in top-right inside the court area
    if (showLegend) {
      const legendPadding = 8;
      const legendW = Math.min(240, courtPixelWidth * 0.45);
      const legendH = 56;
      const legendX = courtPixelWidth - legendW - legendPadding;
      const legendY = legendPadding;

      // Background (rounded with shadow)
      const rr = 10;
      const drawRoundedPanel = (x, y, w, h, r) => {
        const r2 = Math.min(r, w / 2, h / 2);
        ctx.beginPath();
        ctx.moveTo(x + r2, y);
        ctx.lineTo(x + w - r2, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + r2);
        ctx.lineTo(x + w, y + h - r2);
        ctx.quadraticCurveTo(x + w, y + h, x + w - r2, y + h);
        ctx.lineTo(x + r2, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - r2);
        ctx.lineTo(x, y + r2);
        ctx.quadraticCurveTo(x, y, x + r2, y);
        ctx.closePath();
      };

      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.22)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetY = 2;
      drawRoundedPanel(legendX, legendY, legendW, legendH, rr);
      ctx.fillStyle = 'rgba(255,255,255,0.75)';
      ctx.fill();
      ctx.restore();

      drawRoundedPanel(legendX, legendY, legendW, legendH, rr);
      ctx.strokeStyle = 'rgba(0,0,0,0.18)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Items: Most, Medium, Least (colors indicated by swatches)
      const sw = 12, sh = 12, gap = 6;
      let lx = legendX + legendPadding;
      let ly = legendY + legendPadding + 4;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.save();
      ctx.globalAlpha = 0.95;
      ctx.font = 'bold 10px sans-serif';

      // Red swatch - Most
      ctx.fillStyle = 'rgba(255,0,0,0.9)';
      ctx.fillRect(lx, ly - sh + 2, sw, sh);
      ctx.fillStyle = '#0b0b0b';
      ctx.fillText('Most', lx + sw + gap, ly);

      // Orange swatch - Medium
      lx = legendX + legendPadding;
      ly += sh + 6;
      ctx.fillStyle = 'rgba(255,165,0,0.9)';
      ctx.fillRect(lx, ly - sh + 2, sw, sh);
      ctx.fillStyle = '#0b0b0b';
      ctx.fillText('Medium', lx + sw + gap, ly);

      // Yellow swatch - Least
      lx = legendX + legendPadding;
      ly += sh + 6;
      ctx.fillStyle = 'rgba(255,255,0,0.9)';
      ctx.fillRect(lx, ly - sh + 2, sw, sh);
      ctx.fillStyle = '#0b0b0b';
      ctx.fillText('Least', lx + sw + gap, ly);
      ctx.restore();
    }

    ctx.restore();
  }, [data, canvasSize.width, canvasSize.height, style, playerFilter, percentileClamp, gridSize.cols, gridSize.rows, showLegend]);

  const containerStyle = {
    backgroundColor: '#15803d',
    display: 'block',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
    borderRadius: '1rem',
    border: '1px solid rgba(255,255,255,0.1)',
    width: `${width}px`,
    height: `${height}px`,
    position: 'relative',
    overflow: 'hidden',
    ...style,
  };

  return (
    <div ref={containerRef} className={styles.canvasContainer} style={containerStyle}>
      <canvas ref={canvasRef} width={canvasSize.width} height={canvasSize.height} className={styles.canvas} />
    </div>
  );
});

export default OccupancyHeatmapCanvas;
